// ---------------------------------------------------------------------------
// EvalStatsService — HTTP-backed access to historical metric statistics.
//
// Talks to /tools/EvalStats on the configured API base URL. The response
// shape mirrors the C# DTOs in
// src/services/WorkflowSvc/Api/Contracts/EvalStats (camelCase JSON via
// ASP.NET Core defaults). Caching of the underlying distribution lives in the
// service (WorkflowSvc); this client just requests and shapes the result.
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import type { EvalStats } from '../types/evalStats.js';

const ROUTES = {
  query: 'tools/EvalStats/query',
} as const;

/** Response envelope returned by `POST tools/EvalStats/query`. */
interface GetEvalStatsResponse {
  evals: EvalStats[];
}

/**
 * Fetches historical statistics for the given metric names, scoped to a single
 * agent. Statistics are partitioned by agent friendly name because the same
 * metric name (e.g. `Accuracy`) can appear under multiple agents.
 *
 * Metrics with no available statistics are omitted by the API, so the returned
 * map only contains entries that had data. Lookups are keyed by the exact
 * metric name supplied; callers should treat a missing key as "no stats" and
 * fall back to heuristics.
 */
export async function getEvalStats(
  client: IHttpClient,
  agentFriendlyName: string,
  evalNames: readonly string[],
): Promise<Map<string, EvalStats>> {
  const unique = Array.from(new Set(evalNames)).filter((n) => n.length > 0);
  if (agentFriendlyName.length === 0 || unique.length === 0) {
    return new Map();
  }

  const { data } = await client.post<GetEvalStatsResponse>(ROUTES.query, {
    agentFriendlyName,
    evalNames: unique,
  });

  const stats = new Map<string, EvalStats>();
  for (const entry of data.evals) {
    stats.set(entry.evalName, entry);
  }
  return stats;
}
